import { ApiService, ApiSyncType } from "@incendium/api";
import { ArrowForward } from "@mui/icons-material";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import WooLogo from "Assets/Images/ThirdParty/woocommerce-logo-color-black@2x.png";
import WooCommerceConnectButton from "features/dataConnectors/components/WooCommerceConnectButton";
import OnboardingSuperAdminSkipButton from "features/onboarding/components/OnboardingSuperAdminSkipButton";
import WooCommerceOnboardingSecurityNote from "features/onboarding/woocommerce/WooCommerceOnboardingSecurityNote";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import { normalizeStoreUrl } from "features/dataConnectors/utils/normalizeStoreUrl";
import { resolveWooCommerceStoreUrl } from "features/dataConnectors/utils/woocommerceStoreMeta";
import { useSelectedProject } from "Hooks";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useSyncClients } from "Hooks/useSyncClients";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IEcomPlatformOnboardingStep1Props {
  onConnected: () => void;
}

function EcomPlatformOnboardingStep1({
  onConnected,
}: IEcomPlatformOnboardingStep1Props) {
  const { selectedProject } = useSelectedProject();
  const { syncClients, setSyncClients } = useSyncClients();
  const { externalSyncs } = useExternalSyncs();

  const defaultStoreUrl = useMemo(() => {
    const domain = (selectedProject?.domains || []).find(
      (d) => d.isPrimary && d.domain,
    )?.domain;
    return domain ? normalizeStoreUrl(domain) : "";
  }, [selectedProject?.domains]);

  const [storeUrl, setStoreUrl] = useState(defaultStoreUrl);
  const oauthSyncRedirect = OAUTH_REDIRECT_URL;

  const wooSync = useMemo(
    () =>
      externalSyncs.find((s) => s.type === ApiSyncType.WOOCOMMERCE_CONNECTOR),
    [externalSyncs],
  );
  const isConnected = !!wooSync?.client;

  const connectedStoreUrl = useMemo(
    () =>
      resolveWooCommerceStoreUrl(
        externalSyncs,
        defaultStoreUrl || undefined,
      ),
    [externalSyncs, defaultStoreUrl],
  );

  useEffect(() => {
    if (isConnected && connectedStoreUrl) {
      setStoreUrl(connectedStoreUrl);
    }
  }, [isConnected, connectedStoreUrl]);

  const handleConnected = useCallback(() => {
    onConnected();
  }, [onConnected]);

  return (
    <Stack
      spacing={3}
      justifyContent="space-between"
      sx={{ minHeight: "100%" }}
    >
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src={WooLogo}
            alt="WooCommerce"
            sx={{ height: 32, width: "auto" }}
          />
        </Stack>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Store URL
          </Typography>
          <TextField
            fullWidth
            name="storeUrl"
            value={storeUrl}
            variant="outlined"
            placeholder="https://yourstore.com"
            onChange={(e) => setStoreUrl(e.target.value)}
            disabled={isConnected}
          />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {isConnected
              ? "Your WooCommerce store is already connected to this project."
              : "We'll use this address to securely connect and scan your WooCommerce store."}
          </Typography>
        </Box>

        <WooCommerceOnboardingSecurityNote>
          Your connection is secure and encrypted.
          <br />
          We never store your password.
        </WooCommerceOnboardingSecurityNote>
      </Stack>

      <Stack spacing={1}>
        {isConnected ? (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            endIcon={<ArrowForward />}
            onClick={handleConnected}
          >
            Continue
          </Button>
        ) : (
          <>
            <WooCommerceConnectButton
              service={ApiService.WOOCOMMERCE}
              syncClients={syncClients}
              setSyncClients={setSyncClients}
              state={oauthSyncRedirect}
              linkAccountProjectId={selectedProject?.id as number}
              skipStoreUrlPrompt
              storeUrl={storeUrl}
              onStoreUrlChange={setStoreUrl}
              onConnected={handleConnected}
              connectButtonLabel="Connect"
              connectButtonProps={{
                fullWidth: true,
                size: "large",
                variant: "contained",
                color: "primary",
                endIcon: <ArrowForward />,
              }}
            />
            <OnboardingSuperAdminSkipButton
              onSkip={handleConnected}
              label="Skip — continue without connecting"
            />
          </>
        )}
      </Stack>
    </Stack>
  );
}

export default EcomPlatformOnboardingStep1;
